export const NOTIFICATIONS_CHANNEL_PREFIX = "events/NOTIFICATIONS/";

export const NOTIFICATIONS_PAGE_SIZE = 20;

export const NOTIFICATIONS_BROADCAST_CHANNEL = "enis-notifications";

export const NOTIFICATIONS_BROADCAST_EVENTS = Object.freeze({
  INGEST: "ingest",
  MARK_READ: "mark-read",
  MARK_ALL_READ: "mark-all-read",
  RESET: "reset",
});

export const NOTIFICATION_TYPE_ICON = Object.freeze({
  "TEMPLATE-GENERATE": "huge-doc",
  TRANSACTION: "huge-doc",
  SIGNING: "huge-doc",
  DEFAULT: "sli-notif",
});

export function iconForType(type) {
  if (!type) return NOTIFICATION_TYPE_ICON.DEFAULT;
  return NOTIFICATION_TYPE_ICON[type] || NOTIFICATION_TYPE_ICON.DEFAULT;
}

/**
 * Maps a notification type or category key to a lucide icon name.
 * Returns "Bell" as fallback. Keys are matched case-insensitively
 * against substrings, so backends with snake_case / CamelCase / kebab
 * all resolve.
 */
export const CATEGORY_ICON_RULES = Object.freeze([
  { match: /system|систем/, icon: "Settings" },
  { match: /critic|критич|alert/, icon: "Bell" },
  { match: /report|отч/, icon: "FileText" },
  { match: /ticket|обращ/, icon: "MessageSquare" },
  { match: /knowledge|знани|kb/, icon: "Lightbulb" },
  { match: /calendar|календ/, icon: "Calendar" },
  { match: /chat|чат/, icon: "MessageCircle" },
  { match: /todo|task|задач/, icon: "CheckSquare" },
  { match: /wfm|timer/, icon: "Timer" },
  { match: /misc|разное|other/, icon: "Layers" },
  { match: /meet|call|видео/, icon: "Video" },
  { match: /support|поддерж|help/, icon: "Headphones" },
  { match: /sign|подпис|template|транз|transaction/, icon: "FileSignature" },
]);

export function iconForCategory(key, title) {
  const haystack = `${key || ""} ${title || ""}`.toLowerCase();
  for (const rule of CATEGORY_ICON_RULES) {
    if (rule.match.test(haystack)) return rule.icon;
  }
  return "Bell";
}

/**
 * Resolves a project SvgIcon name for a notification category.
 * Used by the sidebar / nav items that prefer in-house SVG sprites
 * over external lucide components. Falls back to `sli-notif`.
 */
const SVG_CATEGORY_ICON_RULES = Object.freeze([
  { match: /system|систем/, icon: "sli-settings" },
  { match: /critic|alert|опас|danger/, icon: "sli-danger" },
  { match: /calendar|календ/, icon: "sli-calendar" },
  { match: /todo|task|задач/, icon: "sli-list-check" },
  { match: /report|отч/, icon: "sli-list" },
  { match: /ticket|обращ|заявк/, icon: "sli-clipboard" },
  { match: /chat|чат/, icon: "sli-notif" },
  { match: /inbox|входящ/, icon: "sli-inbox" },
  { match: /knowledge|знани|kb/, icon: "sli-info" },
  { match: /meet|call|video|видео/, icon: "sli-camera" },
  { match: /sign|template|транз|подпис|transaction/, icon: "sli-pen" },
]);

export function svgIconForCategory(key, title) {
  const haystack = `${key || ""} ${title || ""}`.toLowerCase();
  for (const rule of SVG_CATEGORY_ICON_RULES) {
    if (rule.match.test(haystack)) return rule.icon;
  }
  return "sli-notif";
}

/**
 * Maps a category to an icon-color variant accepted by NavList.
 * Falls back to "default" (neutral grey) when nothing matches —
 * NavList styling shows that as the muted-foreground tint.
 */
const CATEGORY_VARIANT_RULES = Object.freeze([
  { match: /ai|assistent|assist/, variant: "ai" },
  { match: /meet|call|видео|video/, variant: "meet" },
  { match: /todo|task|задач/, variant: "todo" },
  { match: /calendar|календ/, variant: "calendar" },
  { match: /critic|alert|критич/, variant: "critical" },
  { match: /report|отч/, variant: "reports" },
  { match: /sign|template|транз|подпис|transaction/, variant: "signing" },
]);

export function variantForCategory(key, title) {
  const haystack = `${key || ""} ${title || ""}`.toLowerCase();
  for (const rule of CATEGORY_VARIANT_RULES) {
    if (rule.match.test(haystack)) return rule.variant;
  }
  return "default";
}

/**
 * Deterministic hash → tailwind color class pair for avatar backgrounds.
 * Stable per string so the same user keeps the same color.
 */
const AVATAR_PALETTE = Object.freeze([
  { bg: "bg-amber-300", fg: "text-amber-950" },
  { bg: "bg-sky-300", fg: "text-sky-950" },
  { bg: "bg-emerald-300", fg: "text-emerald-950" },
  { bg: "bg-rose-300", fg: "text-rose-950" },
  { bg: "bg-violet-300", fg: "text-violet-950" },
  { bg: "bg-orange-300", fg: "text-orange-950" },
  { bg: "bg-teal-300", fg: "text-teal-950" },
  { bg: "bg-indigo-300", fg: "text-indigo-950" },
]);

export function colorForSeed(seed = "") {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}
